import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

// Optimized gallery images with smaller sizes (w=400 for gallery)
const galleryImages = [
  {
    id: 1,
    src: "./img/Gallery-01.jpg",
    alt: "Couple portrait 1",
  },
  {
    id: 2,
    src: "./img/Gallery-03.jpg",
    alt: "Couple portrait 2",
  },
  {
    id: 3,
    src: "./img/Gallery-02.jpg",
    alt: "Couple portrait 3",
  },
  {
    id: 4,
    src: "./img/Gallery-04.jpg",
    alt: "Traditional decorations",
  },
  {
    id: 5,
    src: "./img/Gallery-05.jpg",
    alt: "Tea set",
  },
  {
    id: 6,
    src: "./img/Gallery-06.jpg",
    alt: "Double happiness",
  },
];

const ParallaxImageOld = ({ src, alt, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  // Different parallax speeds for variety
  const speeds = [-30, -20, -40, -25, -35, -15];
  const y = useTransform(
    scrollYProgress,
    [0, 1],
    [0, speeds[index % speeds.length]]
  );

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="gallery-item group"
      data-testid={`gallery-item-${index + 1}`}
    >
      <motion.div style={{ y }} className="w-full aspect-[3/4] overflow-hidden">
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </motion.div>

      {/* Hover Overlay */}
      <div className="absolute inset-0 bg-[#8B0000]/0 group-hover:bg-[#8B0000]/20 transition-colors duration-300" />

      {/* Corner Accents on Hover */}
      <div className="absolute top-2 left-2 w-4 sm:w-6 h-4 sm:h-6 border-l border-t border-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="absolute bottom-2 right-2 w-4 sm:w-6 h-4 sm:h-6 border-r border-b border-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

const ParallaxImage = ({ src, alt, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  // Different parallax speeds for variety
  const speeds = [-30, -20, -40, -25, -35, -15];
  const y = useTransform(
    scrollYProgress,
    [0, 1],
    [0, speeds[index % speeds.length]]
  );

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      // 1. THIS locks the outer container to a uniform size and hides overflow
      className="relative w-full aspect-[3/4] md:h-[400px] overflow-hidden group shadow-sm bg-[#FDFBF6]"
      data-testid={`gallery-item-${index + 1}`}
    >
      {/* 2. The inner div is slightly larger (-inset-8) to allow room for the Y translation */}
      <motion.div style={{ y }} className="absolute -inset-8">
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </motion.div>

      {/* Hover Overlay (Ensured z-10 so it stays on top of the image) */}
      <div className="absolute inset-0 bg-[#8B0000]/0 group-hover:bg-[#8B0000]/20 transition-colors duration-300 z-10" />

      {/* Corner Accents on Hover (Ensured z-20 so they stay on top) */}
      <div className="absolute top-4 left-4 w-4 sm:w-6 h-4 sm:h-6 border-l border-t border-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20" />
      <div className="absolute bottom-4 right-4 w-4 sm:w-6 h-4 sm:h-6 border-r border-b border-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20" />
    </motion.div>
  );
};

const Gallery = () => {
  return (
    <section
      className="section-wrapper py-16 sm:py-20 md:py-32 px-4 sm:px-6 bg-white/30"
      data-testid="gallery-section"
    >
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-12"
        >
          <p className="font-serif-accent text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.3em] text-[#D4AF37] uppercase mb-3 sm:mb-4">
            Our Moments
          </p>
          <h2 className="font-serif-heading text-2xl sm:text-3xl md:text-5xl text-[#8B0000] mb-3 sm:mb-4">
            Gallery
          </h2>
          <p className="font-sans text-xs sm:text-sm text-[#5A5A5A] max-w-md mx-auto px-4">
            Capturing the precious moments of our journey together
          </p>
        </motion.div>

        {/* Masonry Gallery */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
          {galleryImages.map((image, index) => (
            <ParallaxImage
              key={image.id}
              src={image.src}
              alt={image.alt}
              index={index}
            />
          ))}
        </div>
        {/* <div className="gallery-masonry">
          {galleryImages.map((image, index) => (
            <ParallaxImage
              key={image.id}
              src={image.src}
              alt={image.alt}
              index={index}
            />
          ))}
        </div> */}

        {/* Decorative Element */}
        {/* <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex justify-center mt-8 sm:mt-12"
        >
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-8 sm:w-12 h-px bg-[#D4AF37]" />
            <span className="double-happiness text-xl sm:text-2xl text-[#D4AF37] opacity-60">囍</span>
            <div className="w-8 sm:w-12 h-px bg-[#D4AF37]" />
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default Gallery;
